/*
 * Copyright (c) 2025 Rembrand Pardo
 * Unauthorized copying, modification, or redistribution is strictly prohibited.
 * Violators will face legal action.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/HomePage.css';
import { logEvent } from "firebase/analytics";
import { analytics } from '../firebaseConfig';
import rembrandPardoImage from '../assets/rembrand_pardo_removebg.png';
import { FlipWords } from '../components/FlipWords';
import { TypewriterEffect } from '../components/TypeWritterEffect';
import { HoverBorderGradient } from "../components/HoverBorderGradient";

import '../styles/GradientBackground.css';


import { motion } from 'framer-motion'; 

function HomePage( {translations }) {

  //firebase analytics log
  const handleButtonClick = async () => {
    console.log("Learn More button clicked");
    logUserActivity("Home Page 'Learn More' button clicked");
  };
  const logUserActivity = (activity) => {
    logEvent(analytics, "user_activity", { event: activity });
    console.log(`Analytics Event Logged: ${activity}`);
  };

  return (
    <div className="homepage">
      
      <Helmet>
        <link rel="icon" type="image/png" href="https://rembrandpardo.com/favicon.ico"/>
        <title>Home - Pardo</title>
        <meta name="description" content="Rembrand Pardo is a business strategist, executive leader, and consultant based in Utah, US, with roots in Barcelona, Spain. He specializes in management, leadership, and technology-driven innovation, helping businesses streamline operations, drive growth, and implement effective strategies." />
        <meta name="keywords" content="Rembrand Pardo, business strategy, management, leadership, technology solutions, project management, software development, business development, executive leadership, politics" />
      </Helmet>

      <div className="background-gradient" /> {/* Apply the gradient background */}
      
      {/* Center the content but left-align it */}
      <div className="content-wrapper">
        {/* Typewriter effect for 'Hello world!' */}
        <div className="typewriter-container">
          <TypewriterEffect words={[{text: translations.helloWorld}]} />
        </div>

        {/* Small text "I am" */}
        <motion.div
          className="small-text"
          initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }} // Start with blur and small scale
          animate={{ opacity: 1, scale: 1, filter: "blur(0.1px)" }} // Smoothly fade in, remove blur, and scale up
          transition={{ duration: 1.5, ease: "easeOut", delay: 2.9 }} // Adjust timing for smooth effect, 3-second delay
        >
          {translations.iAm}
        </motion.div>

        {/* Image fades in after typewriter with tech-like effect */}
        <motion.div 
          className="image-container"
          initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }} // Start with blur and small scale
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} // Smoothly fade in, remove blur, and scale up
          transition={{ duration: 1.5, ease: "easeOut", delay: 3.5 }} // Adjust timing for smooth effect, 3-second delay
        >
          <img src={rembrandPardoImage} alt="Rembrand Pardo" className="rembrand-image" />
        </motion.div>

        {/* Flip words effect */}
        <motion.div 
          className="flip-words-container"
          initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }} // Start with blur and small scale
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} // Smoothly fade in, remove blur, and scale up
          transition={{ duration: 1.5, ease: "easeOut", delay: 5.5 }} // Adjust timing for smooth effect, 3-second delay
        >
          {translations.introText}<FlipWords words={translations.changingWords} />
        </motion.div>

        <motion.div 
          className="learn-more-button bg-transparent flex justify-start text-left"
          initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }} // Start with blur and small scale
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} // Smoothly fade in, remove blur, and scale up
          transition={{ duration: 1.5, ease: "easeOut", delay: 6 }} // Adjust timing for smooth effect, 3-second delay
        >
          <a href="/service"> 
            <HoverBorderGradient
              containerClassName="rounded-full"
              as="button"
              className="bg-transparent text-white flex items-center space-x-2"
              onClick={handleButtonClick}
            >
              <span>{translations.learnMore}</span>
            </HoverBorderGradient>
          </a>
        </motion.div>

      </div>


      {/* Hidden SEO text moved to the very bottom of the page */}
      <div className="seo-text" aria-hidden="true">
      Rembrand Pardo is a highly skilled executive, manager, and business leader from Barcelona, Spain, currently based in Utah, US. He specializes in management, leadership, and business strategy, with expertise spanning project management, technology solutions, and business development.
      With years of dedicated education and experience, he has developed deep knowledge in his field, making him an expert in driving innovation and strategic growth. His focus and passion revolve around technology, business, and people, continuously striving to create impactful solutions that bridge these three areas.
      </div>

    </div>
  );
}

export default HomePage;
